/**
 * DemoSandboxTour — Iter-95 · 3-step guided tour for the PM demo sandbox.
 *
 * Auto-launches on first entry into the PM demo dashboard. Fires exactly
 * once per demo session token (persisted in localStorage). The DEMO
 * SANDBOX watermark stays visible in the corner throughout.
 *
 * Steps (spec-locked):
 *   1. Property Overview        — amenities + gate code pre-filled
 *   2. Service Schedule         — upcoming calendar + route ETA widget
 *   3. Resident Portal View     — what tenants see in their portal
 *
 * Skip Tour is always visible in the footer.
 */
import { useEffect, useMemo, useState } from 'react';
import { Building2, CalendarClock, Users2, ArrowRight, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  {
    key: 'property',
    icon: Building2,
    title: 'Property Overview',
    line: "This is your Property Overview — amenities, gate codes, and unit counts are pre-filled from your registration so your first shift can start right away.",
    highlight: 'Amenities · Gate Code · Unit Count',
  },
  {
    key: 'schedule',
    icon: CalendarClock,
    title: 'Service Schedule',
    line: "Your Service Schedule shows upcoming valet or cleaning shifts alongside live route ETAs so you always know when the crew is on their way.",
    highlight: 'Upcoming Calendar · Live Route ETA',
  },
  {
    key: 'resident',
    icon: Users2,
    title: 'Resident Portal View',
    line: "Here's what your residents see in their portal — pickup schedule, missed-pickup requests, and QR-based unit registration. Everything is branded for your property.",
    highlight: 'Tenant Portal · QR Registration · Requests',
  },
];

export default function DemoSandboxTour() {
  const [session, setSession] = useState({ is_demo: false, token: '' });
  const [stepIdx, setStepIdx] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/commercial/demo-session`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled || !d.is_demo) return;
        // Session token is the idempotency key so a page refresh
        // doesn't relaunch the tour.
        const token = (
          (typeof document !== 'undefined' && (document.cookie.match(/(?:^|;\s*)session_token=([^;]+)/)?.[1])) ||
          (typeof localStorage !== 'undefined' && localStorage.getItem('session_token')) ||
          `demo_${d.demo_reg_id || 'unknown'}`
        );
        const seenKey = `necs_demo_tour_seen_${token}`;
        const already = (typeof localStorage !== 'undefined') && localStorage.getItem(seenKey) === '1';
        setSession({ is_demo: true, token });
        if (!already) setOpen(true);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const markSeen = () => {
    try { localStorage.setItem(`necs_demo_tour_seen_${session.token}`, '1'); }
    catch { /* ignore */ }
  };

  const next = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx((n) => n + 1);
    else finish();
  };
  const skip = () => finish();
  const finish = () => {
    markSeen();
    setOpen(false);
  };

  const step = useMemo(() => STEPS[stepIdx], [stepIdx]);

  if (!session.is_demo || !open) return null;
  const Icon = step.icon;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      data-testid="demo-tour-overlay"
    >
      <div
        className="w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden"
        style={{ backgroundColor: '#141414', borderColor: '#C9A962' }}
        data-testid="demo-tour-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#2a2a2a' }}>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#C9A962' }}>
              Demo Sandbox Tour
            </span>
            <span className="text-[10px] text-zinc-500" data-testid="demo-tour-step-counter">
              Step {stepIdx + 1} of {STEPS.length}
            </span>
          </div>
          <button
            type="button"
            onClick={skip}
            className="text-zinc-500 hover:text-white transition"
            title="Skip tour"
            data-testid="demo-tour-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(201,169,98,0.15)' }}
            >
              <Icon className="h-6 w-6" style={{ color: '#C9A962' }} />
            </div>
            <h3
              className="text-2xl font-black text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid={`demo-tour-title-${step.key}`}
            >
              {step.title.toUpperCase()}
            </h3>
          </div>
          <p className="text-sm text-zinc-300 mb-4" data-testid={`demo-tour-line-${step.key}`}>
            {step.line}
          </p>
          <p
            className="inline-block px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest font-bold border"
            style={{ color: '#C9A962', borderColor: 'rgba(201,169,98,0.4)', backgroundColor: 'rgba(201,169,98,0.08)' }}
          >
            {step.highlight}
          </p>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t" style={{ borderColor: '#2a2a2a' }}>
          <button
            type="button"
            onClick={skip}
            className="text-xs text-zinc-400 hover:text-white transition"
            data-testid="demo-tour-skip"
          >
            Skip Tour
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: i <= stepIdx ? '#C9A962' : '#3f3f46' }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            className="px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-1.5"
            style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
            data-testid="demo-tour-next"
          >
            {stepIdx === STEPS.length - 1 ? 'Finish' : 'Next'}
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
